import { useEffect, useState } from "react";
import { DataTable, DataTableField, DataTableCustomActions } from "../../../components/data/DataTable";
import Button from "../../../components/base/Button";
import Modal from "../../../components/base/Modal";
import Icon from "../../../components/base/Icon";
import FlexRow from "../../../components/base/FlexRow";
import { toastSuccess } from "../../../components/base/Toast";
import { API_ROUTES } from "../../../core/Constants";
import { toPrettyDate, useHub } from "../../../core/Global";
import User from "../models/User";
import AddMembersDrawer, { PickItem } from "./AddMembersDrawer";


interface IGroup {
    id?: string
    name?: string
}

interface IProps {
    userId?: string
    model?: User
}

export default function AccountUserGroups(props: IProps) {

    const hub = useHub();

    const [refresh, refreshSet] = useState<boolean>(false);
    const [addOpen, addOpenSet] = useState<boolean>(false);

    const [pool, poolSet] = useState<PickItem[]>([]);
    const [assigned, assignedSet] = useState<string[]>([]);

    const [removeModal, removeModalSet] = useState<boolean>(false);
    const [pendingRemove, pendingRemoveSet] = useState<{ ids: string[], label: string }>();

    useEffect(() => {
        if (refresh) setTimeout(() => refreshSet(false), 800);
    }, [refresh])

    const fetchPool = () => {
        hub.Get(API_ROUTES.Users + "usergroups/" + props.userId, res => {
            if (res.success) {
                const all: IGroup[] = res.data.all ?? [];
                poolSet(all.filter(g => g.id).map(g => ({ id: g.id!, title: g.name })));
                assignedSet(res.data.assigned ?? []);
            }
        });
    }

    useEffect(() => {
        fetchPool();
    }, [props.userId])

    const afterChange = () => {
        refreshSet(true);
        fetchPool();
    }


    const addGroups = (groupIds: string[]) => {
        hub.Post(API_ROUTES.Users + "add-groups/" + props.userId, groupIds, res => {
            if (res.success) {
                toastSuccess("User groups have been updated successfully!");
                afterChange();
            }
        });
    }

    const askRemoveSingle = (data: any) => {
        pendingRemoveSet({ ids: [data.id], label: data.name });
        removeModalSet(true);
    }

    const askRemoveMany = (rows: any[]) => {
        const ids = (rows ?? []).map(r => r.id).filter(Boolean);
        if (ids.length == 0) return;
        pendingRemoveSet({ ids, label: `${ids.length} group${ids.length > 1 ? "s" : ""}` });
        removeModalSet(true);
    }

    const doRemove = (callback?: (res?: any) => void) => {
        const ids = pendingRemove?.ids ?? [];
        hub.Post(API_ROUTES.Users + "remove-groups/" + props.userId, ids, res => {
            if (res.success) {
                toastSuccess("User groups have been updated successfully!");
                afterChange();
            }
            if (callback) callback(res);
        });
    }


    const CreatedItem = (data: any) => (
        <div>
            <div className="d-flex align-items-center">
                {data.groupJoinedAt ? toPrettyDate(data.groupJoinedAt) : <span className="text-muted">—</span>}
            </div>
            {
                data.groupJoinedByName &&
                <FlexRow className="fs-8 mt-1">
                    <Icon icon="user" className="me-2" size={10} type="solid" />
                    <div className="fs-8">By: {data.groupJoinedByName}</div>
                </FlexRow>
            }
        </div>
    )

    const RemoveAction = (data: any) => (
        <div className="menu-item">
            <a href="#" className="menu-link px-3 text-danger"
                onClick={e => {
                    e.preventDefault();
                    askRemoveSingle(data);
                }}>
                <Icon icon="xmark" className="me-2" />
                Remove from group
            </a>
        </div>
    )


    return (
        <>
            <div className="card">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">User Groups</h3>
                    </div>
                    <div className="card-action">
                        <Button icon="plus" text="Add Group" onClick={() => addOpenSet(true)} />
                    </div>
                </div>
                <div className="card-body border-top pt-2">
                    <DataTable noCard preventCreate objectName="Group"
                        apiRoute={API_ROUTES.Users + "groups-of/" + props.userId}
                        refresh={refresh}
                        groupActions={[{
                            title: "Remove from group",
                            icon: "circle-minus",
                            iconClass: "text-danger",
                            action: askRemoveMany
                        }]}>

                        <DataTableField title="Name" dataValueName="name" />
                        <DataTableField title="Added">
                            <CreatedItem />
                        </DataTableField>

                        <DataTableCustomActions>
                            <RemoveAction />
                        </DataTableCustomActions>

                    </DataTable>
                </div>
            </div>

            {
                removeModal && pendingRemove &&
                <Modal id="modal-remove-user-group"
                    title="Remove from Group"
                    buttonText="Remove"
                    open visible staticBackdrop
                    onClose={() => {
                        removeModalSet(false);
                        pendingRemoveSet(undefined);
                    }}
                    onSubmit={(body, callback) => {
                        doRemove(res => {
                            // close via the modal's own script (removes the backdrop),
                            // then onClose unmounts it — never unmount directly.
                            if (res?.success) window.closeModal("modal-remove-user-group");
                            if (callback) callback(res);
                        });
                    }}>
                    <div className="my-5">
                        Are you sure you want to remove <b>{pendingRemove.label}</b> from this user?
                    </div>
                </Modal>
            }

            {
                addOpen &&
                <AddMembersDrawer
                    drawerId="drawer-add-user-groups"
                    icon="users"
                    drawerTitle="Add Groups"
                    addLabel="Add Group"
                    memberLabel="Member"
                    items={pool}
                    selectedIds={assigned}
                    onAddMany={addGroups}
                    onClose={() => addOpenSet(false)} />
            }
        </>
    )
}
